import type { GameTime } from '@/IsekaiRPG/modules/world/worldTypes';
import {
  GAME_TIME_ORDER_METADATA_KEY,
  GAME_TIME_SNAPSHOT_METADATA_KEY,
  GameTimeSnapshotSchema,
  buildGameTimeMetadata,
  formatCompactGameTime,
  toGameTimeOrder,
  type GameTimeSnapshot,
} from '@/IsekaiRPG/modules/world/gameTimeSnapshot';
import type { StoryHistoryEntry } from './historyTypes';

// ============================================
// 时间快照工具转发（实现已下沉 world/gameTimeSnapshot）
// ============================================

// 保留历史导出名，避免既有消费方（conversation、history 内部服务等）改动。
export const STORY_HISTORY_GAME_TIME_SNAPSHOT_KEY = GAME_TIME_SNAPSHOT_METADATA_KEY;
export const STORY_HISTORY_GAME_TIME_ORDER_KEY = GAME_TIME_ORDER_METADATA_KEY;

export type StoryHistoryGameTimeSnapshot = GameTimeSnapshot;

export const formatCompactStoryGameTime = formatCompactGameTime;
export const buildStoryHistoryGameTimeMetadata = buildGameTimeMetadata;
export const toStoryHistoryGameTimeOrder = toGameTimeOrder;

// ============================================
// 游戏时间文本解析（history 专属：解析历史条目里的时间文案）
// ============================================

const SEASON_LABEL_MAP: Record<string, GameTime['season']> = {
  spring: 'spring',
  summer: 'summer',
  autumn: 'autumn',
  winter: 'winter',
  春季: 'spring',
  夏季: 'summer',
  秋季: 'autumn',
  冬季: 'winter',
};

type TimelineComparableEntry = Pick<StoryHistoryEntry, 'id' | 'timestamp' | 'gameTime' | 'metadata'>;

function normalizeSeasonLabel(value: string): GameTime['season'] | null {
  const normalized = value.trim();
  return SEASON_LABEL_MAP[normalized] ?? null;
}

function resolveSeasonByMonth(month: number): GameTime['season'] {
  if (month <= 3) return 'spring';
  if (month <= 6) return 'summer';
  if (month <= 9) return 'autumn';
  return 'winter';
}

function parseStructuredGameTime(value: string): StoryHistoryGameTimeSnapshot | null {
  const fullMatch =
    /^第(?<year>\d+)年\s*(?<season>春季|夏季|秋季|冬季|spring|summer|autumn|winter)\s*[· ]\s*(?<month>\d+)月\s*(?<day>\d+)日\s*[· ]\s*(?<period>凌晨|上午|下午|夜晚)?\s*(?<hour>\d{1,2}):(?<minute>\d{2})$/u.exec(
      value,
    );
  if (!fullMatch?.groups) return null;

  const season = normalizeSeasonLabel(fullMatch.groups.season || '');
  if (!season) return null;

  const parsed = GameTimeSnapshotSchema.safeParse({
    year: Number(fullMatch.groups.year),
    season,
    month: Number(fullMatch.groups.month),
    day: Number(fullMatch.groups.day),
    hour: Number(fullMatch.groups.hour),
    minute: Number(fullMatch.groups.minute),
  });
  return parsed.success ? parsed.data : null;
}

function parseCompactGameTime(value: string): StoryHistoryGameTimeSnapshot | null {
  const compactMatch = /^(?:第)?(?<month>\d+)月\s*(?<day>\d+)日\s+(?<hour>\d{1,2}):(?<minute>\d{2})$/u.exec(value);
  if (!compactMatch?.groups) return null;

  const month = Number(compactMatch.groups.month);
  const parsed = GameTimeSnapshotSchema.safeParse({
    year: 1,
    season: resolveSeasonByMonth(month),
    month,
    day: Number(compactMatch.groups.day),
    hour: Number(compactMatch.groups.hour),
    minute: Number(compactMatch.groups.minute),
  });
  return parsed.success ? parsed.data : null;
}

function parseStatusBarGameTime(value: string): StoryHistoryGameTimeSnapshot | null {
  const statusMatch =
    /^第(?<year>\d+)年\s*(?<month>\d+)月\s*(?<day>\d+)日\s*(?<hour>\d{1,2}):(?<minute>\d{2})$/u.exec(
      value,
    );
  if (!statusMatch?.groups) return null;

  const month = Number(statusMatch.groups.month);
  const parsed = GameTimeSnapshotSchema.safeParse({
    year: Number(statusMatch.groups.year),
    season: resolveSeasonByMonth(month),
    month,
    day: Number(statusMatch.groups.day),
    hour: Number(statusMatch.groups.hour),
    minute: Number(statusMatch.groups.minute),
  });
  return parsed.success ? parsed.data : null;
}

function resolveSnapshotFromMetadata(metadata?: Record<string, unknown>): StoryHistoryGameTimeSnapshot | null {
  const snapshot = metadata?.[STORY_HISTORY_GAME_TIME_SNAPSHOT_KEY];
  const parsed = GameTimeSnapshotSchema.safeParse(snapshot);
  return parsed.success ? parsed.data : null;
}

function resolveOrderFromMetadata(metadata?: Record<string, unknown>): number | null {
  const rawOrder = metadata?.[STORY_HISTORY_GAME_TIME_ORDER_KEY];
  return typeof rawOrder === 'number' && Number.isFinite(rawOrder) ? rawOrder : null;
}

export function parseStoryHistoryGameTime(value?: string | null): StoryHistoryGameTimeSnapshot | null {
  if (!value?.trim()) return null;

  return (
    parseStructuredGameTime(value.trim()) ?? parseStatusBarGameTime(value.trim()) ?? parseCompactGameTime(value.trim())
  );
}

// ============================================
// 时间线排序
// ============================================

export function resolveStoryHistoryGameTimeSnapshot(
  entry: Pick<TimelineComparableEntry, 'gameTime' | 'metadata'>,
): StoryHistoryGameTimeSnapshot | null {
  return resolveSnapshotFromMetadata(entry.metadata) ?? parseStoryHistoryGameTime(entry.gameTime);
}

export function resolveStoryHistoryGameTimeOrder(
  entry: Pick<TimelineComparableEntry, 'gameTime' | 'metadata'>,
): number | null {
  const metadataOrder = resolveOrderFromMetadata(entry.metadata);
  if (metadataOrder !== null) return metadataOrder;

  const snapshot = resolveStoryHistoryGameTimeSnapshot(entry);
  return snapshot ? toGameTimeOrder(snapshot) : null;
}

export function compareStoryHistoryTimeline(left: TimelineComparableEntry, right: TimelineComparableEntry): number {
  const leftOrder = resolveStoryHistoryGameTimeOrder(left);
  const rightOrder = resolveStoryHistoryGameTimeOrder(right);

  if (leftOrder !== null && rightOrder !== null && leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  if (left.timestamp !== right.timestamp) {
    return left.timestamp - right.timestamp;
  }

  return left.id.localeCompare(right.id);
}
